import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, OnDestroy, inject, input, ChangeDetectionStrategy, signal } from '@angular/core';
import { ReplaySubject, takeUntil } from 'rxjs';

import Social from 'entity/Social';
import User from 'entity/User';
import { SocialEventType } from 'messages/websocket.messages';

import { ChatService } from '../services/chat.service';
import { SideSectionService } from '../services/side-section.service';
import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';

import { SideContainerType } from './enums/SideContainerType';
import { SubMenu, SubMenuType } from './social-menu.types';

const ANIMATION_TIMINGS = '300ms cubic-bezier(0.32, 0, 0.67, 0)';

@Component({
  selector: 'app-social-menu',
  templateUrl: './social-menu.component.html',
  styleUrls: ['./social-menu.component.scss'],
  animations: [
    trigger('slideContent', [
      state('void', style({ transform: 'translateX(250px)', opacity: 0.75 })),
      state('enter', style({ transform: 'translateX(0px)', opacity: 1 })),
      transition('* => *', animate(ANIMATION_TIMINGS)),
    ]),
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SocialMenuComponent implements OnInit, OnDestroy {
  private sideSectionService = inject(SideSectionService);
  private chatService = inject(ChatService);

  public readonly isHost = input<boolean>();
  public readonly user = input<User>();
  public readonly social = input<Social>();

  protected readonly subMenus = signal<SubMenu[]>([]);
  protected readonly currentSubMenu = signal<SubMenu | undefined>(undefined);
  protected readonly headerTitle = signal('');

  private destroyed$ = new ReplaySubject<boolean>(1);

  public ngOnInit(): void {
    this.sideSectionService.subMenu$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (subMenus: SubMenu[]) => {
        this.subMenus.set(subMenus);
        this.currentSubMenu.set(this.sideSectionService.getOpenSubMenu());
        this.headerTitle.set(this.currentSubMenu()?.title ?? 'Options');
      },
    });

    SocialStateMachine.shared.stateChanged$.pipe(takeUntil(this.destroyed$)).subscribe((socialState) => {
      // Close presentation settings if presentation mode is not active.
      if (
        (this.currentSubMenu()?.type === SubMenuType.PRESENTATION_SETTINGS ||
          this.currentSubMenu()?.type === SubMenuType.AUDIO_SETTINGS ||
          this.currentSubMenu()?.type === SubMenuType.MANAGE_PRESENTERS) &&
        !SocialStateHelper.isEventHappening(socialState, SocialEventType.PRESENTATION)
      ) {
        this.sideSectionService.resetSubMenus();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public get subMenuType(): typeof SubMenuType {
    return SubMenuType;
  }

  public backMenu(): void {
    if (this.currentSubMenu()?.type === SubMenuType.CHAT) {
      this.chatService.resetWindow();
    }
    this.sideSectionService.popSubMenu();
  }

  public closeMenu(): void {
    this.sideSectionService.setOpenState(SideContainerType.NONE);
  }
}
